import React, { useState, useRef } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { ZoomIn, ZoomOut, RotateCcw, Maximize2, SunMedium, Contrast } from 'lucide-react';
import "../assets/css/ImageViewer.css"; 

const ImageViewer = ({ show, onHide, imageUrl }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);

  const imageRef = useRef(null);

  const resetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setRotation(0);
    setBrightness(100);
    setContrast(100);
  };

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.1, 3));
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - 0.1, 0.5);
    setScale(newScale);
    if (newScale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleRotate = () => {
    setRotation(prevRotation => prevRotation + 90);
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = Math.sign(e.deltaY);
    const newScale = Math.max(0.5, Math.min(3, scale - delta * 0.1));
    setScale(newScale);
    if (newScale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      className="image-viewer-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-overlay">Image Viewer</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="relative flex flex-col h-full">
          <div 
            className="overlay p-3 bg-gray-100 border-b absolute top-0 left-0 right-0"
            style={{ zIndex: 10 }}
          >
            <div className="flex justify-center items-center gap-3">
              <ButtonGroup>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleZoomIn}
                  title="Zoom In"
                >
                  <ZoomIn size={20} />
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleZoomOut}
                  title="Zoom Out"
                >
                  <ZoomOut size={20} />
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleRotate}
                  title="Rotate"
                >
                  <RotateCcw size={20} />
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={resetView}
                  title="Reset View"
                >
                  <Maximize2 size={20} />
                </Button>
              </ButtonGroup>
            </div>
            <div className="flex justify-center items-center gap-4 mt-3">
              <div className="flex items-center gap-2">
                <SunMedium size={20} className="text-gray-600" />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={brightness}
                  onChange={(e) => setBrightness(e.target.value)}
                  className="w-24"
                  title="Brightness"
                />
              </div>
              <div className="flex items-center gap-2">
                <Contrast size={20} className="text-gray-600" />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={contrast}
                  onChange={(e) => setContrast(e.target.value)}
                  className="w-24"
                  title="Contrast"
                />
              </div>
            </div>
          </div>

          <div 
            className="flex items-center justify-center bg-gray-900 overflow-hidden"
            style={{ height: 'calc(90vh - 60px)', overflow: 'hidden' }} // Adjust height for overlay toolbar
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}  // Add mouse wheel zoom handler
          >
            {imageUrl && (
              <div 
                className="relative flex items-center justify-center"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  overflow: 'hidden',
                  cursor: scale > 1 ? 'move' : 'default'
                }}
              >
                <img
                  src={imageUrl}
                  ref={imageRef}
                  alt="Medical scan"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                    filter: `brightness(${brightness}%) contrast(${contrast}%)`,
                    transition: scale === 1 ? 'transform 0.2s ease-in-out' : 'none',
                    pointerEvents: 'none'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ImageViewer;
